import { cva, type VariantProps } from 'class-variance-authority'
import React, { FC } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import type { MenuProps } from 'antd'
import { Menu } from 'antd'
import { HomeIcon, BookOpenIcon, MonitorIcon, UsersIcon, BarChart3Icon } from 'lucide-react'
import { APP_HEADER_HEIGHT } from '@/constants/envs/constants'
import IceCubeLogo from '@/assets/image/ice_cube_logo.png'

const items: MenuProps['items'] = [
  {
    label: 'Home',
    key: 'home',
    icon: <HomeIcon size={18} />,
  },
  {
    label: 'Order History',
    key: 'orderHistory',
    icon: <BookOpenIcon size={18} />,
  },
  {
    label: 'Sales Summary',
    key: 'dailySalesSummary',
    icon: <BarChart3Icon size={18} />,
  },
  {
    label: 'Customer',
    key: 'customerManagement',
    icon: <UsersIcon size={18} />,
  },
  {
    label: 'Monitor',
    key: 'monitor',
    icon: <MonitorIcon size={18} />,
    children: [
      {
        label: 'Monitor 1',
        key: 'monitor1',
      },
      {
        label: 'Monitor 2',
        key: 'monitor2',
      },
    ],
  },
]
const navigationContainerVariants = cva(
  `md:pl-7 pl-2 sticky flex items-center gap-4 top-0 z-50 h-[${APP_HEADER_HEIGHT}px] w-full bg-white`,
  {
    variants: {
      variant: {
        default:
          'bg-gray-100 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800',
        transparent: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

interface INavContainerProps
  extends VariantProps<typeof navigationContainerVariants> {
  title?: string
  children: React.ReactNode
}

interface INavProps extends VariantProps<typeof navigationContainerVariants> {
  title?: string
  rightElement?: React.ReactNode
}

const NavigationContainer: React.FC<INavContainerProps> = ({
  children,
  variant,
  title,
}) => {
  return (
    <div className={navigationContainerVariants({ variant })}>
      <RouterLink
        to='/'
        className='flex items-center gap-4 text-nowrap whitespace-nowrap'
      >
        <img src={IceCubeLogo} width='26' />
        <p className='text-lg font-sm font-bold'>{title}</p>
      </RouterLink>
      <div className='flex items-center justify-between gap-4 w-full'>
        {children}
      </div>
    </div>
  )
}

export const Navigation: FC<INavProps> = ({ variant, title }) => {
  const navigate = useNavigate()
  const handleMenuClick: MenuProps['onClick'] = e => {
    if (e.key === 'monitor1') {
      window.open('/monitor/1', '_blank')
    } else if (e.key === 'monitor2') {
      window.open('/monitor/2', '_blank')
    } else if (e.key === 'orderHistory') {
      navigate('/order-history')
    } else if (e.key === 'dailySalesSummary') {
      navigate('/daily-sales-summary')
    } else if (e.key === 'customerManagement') {
      navigate('/customer-management')
    } else if (e.key === 'home') {
      navigate('/')
    }
  }

  return (
    <>
      <style>
        {`
          .ant-menu-horizontal {
            border-bottom: none !important;
          }
          .ant-menu-horizontal > .ant-menu-item,
          .ant-menu-horizontal > .ant-menu-submenu {
            height: ${APP_HEADER_HEIGHT}px !important;
            line-height: ${APP_HEADER_HEIGHT}px !important;
            border-bottom: none !important;
          }
          .ant-menu-submenu-popup {
            z-index: 1050 !important;
          }
          .ant-menu-horizontal > .ant-menu-submenu > .ant-menu-submenu-title {
            height: ${APP_HEADER_HEIGHT}px !important;
            line-height: ${APP_HEADER_HEIGHT}px !important;
          }
          
          /* Fix dropdown hover behavior */
          .ant-menu-submenu:hover .ant-menu-submenu-popup {
            display: block !important;
          }
          
          .ant-menu-submenu:not(:hover) .ant-menu-submenu-popup {
            display: none !important;
          }
          
          /* Ensure smooth transitions */
          .ant-menu-submenu-popup {
            transition: opacity 0.1s ease-in-out !important;
          }
        `}
      </style>
      <NavigationContainer variant={variant} title={title}>
        <div className='w-full grow'>
          <Menu
            items={items}
            onClick={handleMenuClick}
            mode='horizontal'
            className='border-0 bg-transparent'
            style={{
              lineHeight: `${APP_HEADER_HEIGHT}px`,
              borderBottom: 'none',
              height: `${APP_HEADER_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
            }}
            overflowedIndicator={null}
            triggerSubMenuAction='hover'
            subMenuOpenDelay={0.1}
            subMenuCloseDelay={0.1}
          />
        </div>
      </NavigationContainer>
    </>
  )
}
